import { useTranslation, Translation, Trans } from 'react-i18next';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import cx from 'clsx';
import ReactGA4 from 'react-ga4';

import {
  UnionAvatar,
  EmptyRecord,
  Button,
  UserCardPopup,
  UnionFollowButtonV2,
  DateBarChart,
  wrapPageModal,
  VipLogoWithGift,
  DesktopHeader2,
  SideBox,
} from '@/components';
import { getOfficialPath, isOfficalAddress, openNewWindow } from '@/utils';
import { useCurrentAddressInfo, useQueryParams, useUserType } from '@/hooks';
import { OfficialAccountVerifyStatus } from '@/hooks/useOfficialAccount';

import {
  ArticleComments,
  ArticleContent,
  TopContributors,
  useRealizedValues,
  ArticleTip,
} from '../../components';
import { useArticleDisseminationTrend, useFeedArticle } from '../../hooks';
import { FeedArticle, FeedArticleDetail } from '../../types';
import style from './index.module.scss';
import { getDetailPageTitle } from '../../utils';
import { CustomTitle } from '@/components/CustomTitle';
import { FeedMoreAction } from '../../components/FeedMoreAction';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';

type StreamProps = {
  address?: string;
  inModal?: boolean;
  id: string;
  article?: FeedArticleDetail;
};

const TAB_OPTIONS = [
  {
    label: <Trans i18nKey="ArticleComments.1q4xqtw" />,
    value: 'comments',
  },
  {
    label: <Trans i18nKey="DetailContainer.1br2in" />,
    value: 'stats',
  },
  {
    label: <Translation>{t => t('ArticleContent.promotion')}</Translation>,
    value: 'promotion',
  },
];

export const DetailContainer = memo(({ id, inModal, article }: StreamProps) => {
  const { t } = useTranslation();

  const { address } = useCurrentAddressInfo();
  const { data: feed, isLoading } = useFeedArticle(id, article);
  const history = useHistory();
  const userType = useUserType();
  const articleId = Number(id);
  const isOfficial = isOfficalAddress(feed?.article.creator_id);
  const disableAuthorPopup =
    isOfficial &&
    (feed?.article?.creator as OfficialAccount)?.verify_status ===
      OfficialAccountVerifyStatus.INACTIVE;
  const isQuest = !!feed?.article?.quest;

  const titlePrefix = getDetailPageTitle(feed?.article);
  const [mode, setMode] = useQueryParams('mode', TAB_OPTIONS[0].value);

  const handleClick = useCallback(() => {
    history.replace('/stream');
  }, [history]);

  useEffect(() => {
    ReactGA4.event({
      category: 'View Stream',
      action: `Stream_Detail_${userType}`,
    });
  }, [userType]);

  const handleDetailBackClick = useCallback(
    hasHistory => {
      if (hasHistory) {
        history.goBack();
      } else {
        history.replace('/stream');
      }
    },
    [history],
  );

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      if (!feed) {
        return;
      }

      const userId = feed?.article.creator_id;

      openNewWindow(
        isOfficial
          ? `${getOfficialPath(feed?.article?.creator as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [feed, isOfficial],
  );

  const needPaid = useMemo(() => {
    const payThread =
      feed?.article.type === 'pay_thread' &&
      !feed?.article.is_paid &&
      feed?.article.creator_id !== address;

    const payQa =
      feed?.article.type === 'pay_qa' &&
      feed?.article.pay_qa?.answerer?.id !== address &&
      feed?.article.creator_id !== address &&
      !feed?.article.pay_qa?.is_paid;

    // return !!address && (payThread || payQa);
    return payThread || payQa;
  }, [feed?.article, address]);

  const isSuicide = useMemo(
    () => (feed?.article?.creator as GeneralAccount)?.desc?.user?.is_suicide,
    [feed?.article?.creator],
  );

  const disableComment = useMemo(
    () => !feed?.article?.has_permission,
    [feed?.article?.has_permission],
  );

  const pageHeader = inModal ? null : (
    <DesktopHeader2
      title={t('DesktopContainer.ubkbsh')}
      onBackClick={handleDetailBackClick}
      enableBack
    />
  );

  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const { mode: m } = e.currentTarget.dataset;

      if (m && m !== mode) {
        setMode(m);
      }
    },
    [mode, setMode],
  );

  const handlePromotionClick = useCallback(() => {
    setMode(TAB_OPTIONS[2].value);
  }, [setMode]);

  return (
    <>
      <CustomTitle titlePrefix={titlePrefix} />
      <div className={cx(style.stream, inModal && style.inModal)}>
        {!isLoading && (!feed || feed.article.is_withdrawn) ? (
          <div className={style.streamMain}>
            {pageHeader}
            <EmptyRecord
              content={
                <>
                  <div>
                    {feed?.article.is_withdrawn
                      ? t('DetailContainer.hidden')
                      : t('DetailContainer.noexist')}
                  </div>
                  <div>{t('DetailContainer.he82q')}</div>
                  <Button type="primary" className={style.gotoBtn} onClick={handleClick}>
                    {t('DetailContainer.19uqzf1')}
                  </Button>
                </>
              }
            />
          </div>
        ) : (
          <div className={style.streamMain}>
            {pageHeader}
            <div className={cx(feed?.article.is_withdrawn && style.isWithdrawn)}>
              {isLoading ? (
                <>
                  <div className={style.streamHead}>
                    <Skeleton width={48} height={48} circle className={style.avatar} />
                    <div className={style.header}>
                      <div className={style.creator}>
                        <Skeleton width={80} />
                        <Skeleton width={20} />
                      </div>
                      <Skeleton width={120} />
                    </div>
                  </div>
                  <div className={style.contentSkeleton}>
                    <Skeleton height={300} />
                  </div>
                </>
              ) : feed ? (
                <div className={style.streamHead}>
                  <div onClick={feed.article._isBot ? undefined : handleUserClick}>
                    <UnionAvatar
                      className={cx(
                        style.avatar,
                        feed.article._isBot &&
                          !feed.article.monitor_alert?.bot?.is_active &&
                          style.disableAvatarLink,
                      )}
                      size={48}
                      data={feed.article.creator}
                      isBot={feed.article._isBot}
                      isBotActive={feed.article.monitor_alert?.bot?.is_active}
                      userCardPopup={{
                        disabled: disableAuthorPopup,
                        restParams: {
                          source: 'article',
                          article_id: articleId,
                        },
                      }}
                      officialLogoOption={{
                        placement: 'topLeft',
                        align: { targetOffset: [33, 0] },
                        tooltipClassName: style.officialVerifyTip,
                      }}
                    />
                  </div>
                  <div className={style.header}>
                    <div className={style.creator}>
                      <div className={style.creatorLeft}>
                        {feed.article._isBot ? (
                          <div
                            className={cx(
                              style.author,
                              !feed?.article?.monitor_alert?.bot?.is_active && style.isOffline,
                            )}>
                            {feed.article._botName}
                          </div>
                        ) : (
                          <UserCardPopup
                            address={feed.article?.creator?.id || ''}
                            disabled={disableAuthorPopup}
                            restParams={{
                              source: 'article',
                              article_id: articleId,
                            }}>
                            <div onClick={handleUserClick} className={style.author}>
                              {isSuicide ? (
                                <SuicideTag userAddress={feed.article?.creator_id} />
                              ) : (
                                feed.article._author
                              )}
                            </div>
                          </UserCardPopup>
                        )}
                        {feed.article._showVip ? (
                          <VipLogoWithGift
                            userInfo={feed.article.creator as UserInfo}
                            marginRight={8}
                          />
                        ) : null}

                        {feed.article?.creator?.id ? (
                          <UnionFollowButtonV2
                            address={feed.article?.creator?.id}
                            isFollowing={feed.article.creator?.is_following}
                          />
                        ) : null}
                        {feed.article.is_withdrawn && (
                          <span className={style.withdrawn}>{t('DetailContainer.zqt616')}</span>
                        )}
                      </div>
                      <FeedMoreAction articleDetail={feed} />
                    </div>
                    <div className={style.createdAt}>{feed.article._fulltime}</div>
                  </div>
                </div>
              ) : null}
              {feed?.article ? (
                <ArticleContent
                  articleDetail={feed}
                  className={style.content}
                  showRawImage
                  source="detail"
                  enableRefCode
                  enableReadFeedback={true}
                  enableTranslate
                  disableComment={disableComment}
                  onPromotionClick={handlePromotionClick}
                />
              ) : null}
              <div className={style.tabs}>
                {TAB_OPTIONS.map(x => (
                  <div
                    key={x.value}
                    className={style.tabItem}
                    data-mode={x.value}
                    onClick={handleTabClick}>
                    <div className={cx(style.tabInner, mode === x.value && style.isSelected)}>
                      {x.label}
                    </div>
                  </div>
                ))}
              </div>
              {mode === TAB_OPTIONS[0].value ? (
                <ArticleComments
                  feed={feed}
                  disabled={!feed || feed?.article.is_withdrawn || disableComment || needPaid}
                  articleId={articleId}
                  needPaid={needPaid}
                  // containerClassName={mode === TAB_OPTIONS[0].value ? style.show : undefined}
                  containerClassName={style.show}
                />
              ) : null}
              {mode === TAB_OPTIONS[1].value ? (
                <SideStats feed={feed} articleId={articleId} isArticleLoading={isLoading} />
              ) : null}
              {mode === TAB_OPTIONS[2].value ? (
                <ArticleTip article={feed?.article} articleId={articleId} />
              ) : null}
            </div>
          </div>
        )}
        <SideBox isXP={isQuest} />
      </div>
    </>
  );
});

const SideStats = ({
  feed,
  articleId,
  isArticleLoading,
}: {
  feed?: FeedArticleDetail;
  articleId: number;
  isArticleLoading?: boolean;
}) => {
  const { t } = useTranslation();

  const { data: trend, isLoading: isTrendLoading } = useArticleDisseminationTrend(articleId);
  return (
    <div className={style.sideStats}>
      <DateBarChart
        className={style.sidebarChart}
        domainMargin={0.2}
        movePlaceholder={
          <div className={style.dissemination}>
            <div className={style.disseminateTitle}>{t('DetailContainer.pngqyj')}</div>
            <div className={style.disseminateValue}>
              {feed
                ? feed.article._realizedValue
                  ? `$${feed.article._realizedValue}`
                  : t('ArticleContent.tobe')
                : '-'}
            </div>
          </div>
        }
        data={trend}
        isLoading={isTrendLoading}
      />
      <ReadersValue article={feed?.article} isArticleLoading={isArticleLoading} />
      <TopContributors id={articleId} />
    </div>
  );
};

const ReadersValue = ({
  article,
  isArticleLoading,
}: {
  article?: FeedArticle;
  isArticleLoading?: boolean;
}) => {
  const relatedFields = useRealizedValues(article);

  return (
    <div className={style.relatedFields}>
      {isArticleLoading ? (
        <ReadersValueSkeleton />
      ) : (
        relatedFields?.map(x => (
          <div className={style.field} key={x.label}>
            <span className={style.fieldLabel}>{x.label}</span>
            <span className={style.fieldValue}>{x.value}</span>
          </div>
        ))
      )}
    </div>
  );
};

const ReadersValueSkeleton = memo(() => (
  <>
    {Array.from({ length: 3 }).map((x, i) => (
      <div className={style.field} key={i}>
        <Skeleton width={60} />
        <Skeleton width={30} />
      </div>
    ))}
  </>
));

export const openSteamDetailModal = ({
  id,
  article,
}: {
  id: number;
  article?: FeedArticleDetail;
}) => {
  return wrapPageModal({
    title: <Translation>{t => t('DetailContainer.ubkbsh')}</Translation>,
    children: <DetailContainer id={id.toString()} article={article} />,
    link: `/stream/${id}`,
  });
};
